import * as THREE from "three";
import { appearanceMaterials } from "./appearance.ts";
import { loadPieces, pedestalMaterial, type Piece, pieceMaterial } from "./shelf.ts";

/** Places on the title screen's workshop that lead somewhere. */
export type Spot = "letters" | "free" | "shelf" | "ledger" | "howto" | "settings" | "credits";

const mat = (color: number, roughness = 0.75, extra: THREE.MeshStandardMaterialParameters = {}) =>
  new THREE.MeshStandardMaterial({ color, roughness, ...extra });
const WOOD = mat(0xb98252, 0.7);
const WOOD_DARK = mat(0x8a5a3a, 0.75);
const PLASTER = mat(0xf3e2cc, 0.95);
const BRICK = mat(0xb4603f, 0.9);
const BRICK_DARK = mat(0x7a3a26, 0.9);
const PAPER = mat(0xfff6e6, 0.85);
const SEAL = mat(0xd9534f, 0.5);
const INK = mat(0x8a6d62, 0.9);
const COVER = mat(0x5fb7a2, 0.7);
const LEATHER = mat(0x7a4a32, 0.6);
const METAL = mat(0x9aa3a8, 0.35, { metalness: 0.8 });
const SPONGE = mat(0xf2d27a, 0.95);
const CLAY = new THREE.MeshPhysicalMaterial({
  color: 0xf2b48c,
  roughness: 0.55,
  sheen: 0.8,
  sheenRoughness: 0.45,
  sheenColor: new THREE.Color(0xffd6bd),
});
const FIRE = new THREE.MeshBasicMaterial({ color: 0xff8a3d });

/** Floorboards, drawn once. */
function floorTexture(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = 512;
  c.height = 512;
  const g = c.getContext("2d")!;
  for (let k = 0; k < 8; k++) {
    g.fillStyle = k % 2 ? "#c8996e" : "#c19062";
    g.fillRect(0, k * 64, 512, 64);
    g.fillStyle = "rgba(90, 58, 40, 0.35)";
    g.fillRect(0, k * 64, 512, 2);
    // Board ends, staggered.
    g.fillRect((k * 197) % 512, k * 64, 2, 64);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(3, 2);
  return t;
}

/** Views of the workshop: the whole room, and close to each spot. */
const VIEWS: Record<"room" | Spot, { eye: THREE.Vector3; look: THREE.Vector3 }> = {
  room: { eye: new THREE.Vector3(0.1, 2.05, 5.9), look: new THREE.Vector3(0, 1.1, -1.2) },
  letters: { eye: new THREE.Vector3(0.6, 1.55, 1.05), look: new THREE.Vector3(0.62, 0.95, -0.15) },
  free: { eye: new THREE.Vector3(-0.35, 1.9, 1.85), look: new THREE.Vector3(-0.25, 1.1, 0.05) },
  shelf: { eye: new THREE.Vector3(1.55, 1.35, -0.35), look: new THREE.Vector3(2.6, 1.05, -2.4) },
  ledger: { eye: new THREE.Vector3(1.75, 2.75, -1.2), look: new THREE.Vector3(2.3, 2.15, -2.5) },
  howto: { eye: new THREE.Vector3(-0.75, 1.6, 0.95), look: new THREE.Vector3(-0.72, 0.96, 0.22) },
  settings: {
    eye: new THREE.Vector3(-1.45, 2.3, -1.1),
    look: new THREE.Vector3(-1.8, 2.15, -3.15),
  },
  credits: { eye: new THREE.Vector3(0.9, 2.3, -1.3), look: new THREE.Vector3(1.2, 2.35, -3.15) },
};
/** Half the width of the room that the view keeps in, from the kiln to the shelf. */
const ROOM_HALF_WIDTH = 3.3;
/** Seconds to move close to a spot before its screen opens. */
const FLIGHT = 0.7;

/**
 * The title screen: the shop's workshop in 3D, beside the menu (see ui/title.tsx). A lump of
 * clay breathes on the workbench next to a tray of letters, the kiln's fire flickers, and
 * the shelf at the back holds the pieces kept so far. Pointing at a menu item (or at its
 * spot in the room) moves the camera in on it; choosing it opens its screen.
 */
export class Workshop {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(42, 1, 0.1, 60);
  private readonly spots: Record<Spot, THREE.Group> = {
    letters: new THREE.Group(),
    free: new THREE.Group(),
    shelf: new THREE.Group(),
    ledger: new THREE.Group(),
    howto: new THREE.Group(),
    settings: new THREE.Group(),
    credits: new THREE.Group(),
  };
  private readonly lump: THREE.Mesh;
  private readonly fire: THREE.Mesh;
  private readonly shown = new THREE.Group();
  private readonly look = new THREE.Vector3().copy(VIEWS.room.look);
  private view: "room" | Spot = "room";
  private flight: { spot: Spot; left: number } | null = null;
  /** The spot pointed at in the room itself. */
  private hovered: Spot | null = null;
  private time = 0;
  private readonly raycaster = new THREE.Raycaster();
  active = true;
  /** Keeps the camera still in the room, without its gentle drift (see ui/settings.tsx). */
  still = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    environment: THREE.Texture,
    /** The menu's pane, beside which (or above which) the room is drawn. */
    private readonly pane: () => Element | null,
    /** Called as the camera sets off for a spot, and when it gets there. */
    private readonly on: { leave(): void; arrive(spot: Spot): void },
  ) {
    this.scene.environment = environment;
    this.scene.environmentIntensity = 0.5;
    this.scene.background = new THREE.Color(0xf3e2cc);
    this.scene.add(new THREE.HemisphereLight(0xfff1e0, 0xc89a70, 0.9));
    const sun = new THREE.DirectionalLight(0xffe6c4, 1.6);
    sun.position.set(-1.5, 4, -2);
    this.scene.add(sun, ...Object.values(this.spots));
    this.camera.position.copy(VIEWS.room.eye);

    this.buildRoom();
    this.lump = this.buildBench();
    this.fire = this.buildKiln();
    this.buildShelf();
    this.buildCertificate();
    this.buildRack();
    this.spots.shelf.add(this.shown);

    canvas.addEventListener("pointermove", (e) => this.hover(e));
    canvas.addEventListener("click", (e) => {
      if (this.active && !this.flight && this.hover(e)) this.go(this.hovered!);
    });
  }

  /** Back to the room view, with the shelf's pieces as they are now. */
  async enter(): Promise<void> {
    this.view = "room";
    this.flight = null;
    this.active = true;
    let pieces: Piece[] = [];
    try {
      pieces = await loadPieces();
    } catch {
      // No storage: an empty shelf.
    }
    this.fillShelf(pieces.sort((a, b) => b.date - a.date).slice(0, 6));
  }

  /** Moves in on a spot, then opens its screen. */
  go(spot: Spot): void {
    if (!this.active || this.flight) return;
    this.view = spot;
    this.flight = { spot, left: FLIGHT };
    this.on.leave();
  }

  /** Looks at a spot for a while (a menu item pointed at), or back at the whole room. */
  preview(spot: Spot | null): void {
    if (this.active && !this.flight) this.view = spot ?? "room";
  }

  /** The part of the screen the room is drawn in: beside the menu, or above it. */
  private area(): { left: number; top: number; width: number; height: number } {
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    const pane = this.pane()?.getBoundingClientRect();
    if (!pane) return { left: 0, top: 0, width: w, height: h };
    return pane.right < w - 1
      ? { left: pane.right, top: 0, width: w - pane.right, height: h }
      : { left: 0, top: 0, width: w, height: Math.max(1, pane.top) };
  }

  private hover(e: MouseEvent): boolean {
    if (!this.active) return false;
    const a = this.area(), rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left - a.left, y = e.clientY - rect.top - a.top;
    let spot: Spot | null = null;
    if (x >= 0 && y >= 0 && x <= a.width && y <= a.height) {
      this.raycaster.setFromCamera(
        new THREE.Vector2((x / a.width) * 2 - 1, -(y / a.height) * 2 + 1),
        this.camera,
      );
      const spots = Object.entries(this.spots) as [Spot, THREE.Group][];
      const hit = this.raycaster.intersectObjects(spots.map(([, group]) => group), true)[0];
      for (let o: THREE.Object3D | null = hit?.object ?? null; o && !spot; o = o.parent) {
        spot = spots.find(([, group]) => group === o)?.[0] ?? null;
      }
    }
    this.hovered = spot;
    this.canvas.style.cursor = spot ? "pointer" : "";
    return spot !== null;
  }

  update(dt: number): void {
    this.time += dt;
    const a = this.area();
    this.camera.aspect = a.width / a.height;
    this.camera.updateProjectionMatrix();

    // A gentle drift in the room; a quick move in on a spot.
    const target = VIEWS[this.view];
    const eye = target.eye.clone();
    if (this.view === "room") {
      // Narrow areas back off to keep the kiln and the shelf in.
      const away = eye.clone().sub(target.look), vfov = THREE.MathUtils.degToRad(this.camera.fov);
      const fit = ROOM_HALF_WIDTH / (Math.tan(vfov / 2) * this.camera.aspect);
      const distance = Math.max(away.length(), fit);
      eye.copy(target.look).addScaledVector(away.normalize(), distance);
      if (!this.still) {
        eye.x += Math.sin(this.time * 0.21) * 0.18;
        eye.y += Math.sin(this.time * 0.13) * 0.05;
      }
    }
    const k = 1 - Math.exp(-dt * (this.flight ? 7 : 4));
    this.camera.position.lerp(eye, k);
    this.look.lerp(target.look, k);
    this.camera.lookAt(this.look);
    this.camera.updateMatrixWorld();
    if (this.flight && (this.flight.left -= dt) <= 0) {
      const { spot } = this.flight;
      this.flight = null;
      this.active = false;
      this.canvas.style.cursor = "";
      this.on.arrive(spot);
    }

    // The clay breathes; the fire flickers.
    const breath = Math.sin(this.time * 2.1);
    this.lump.scale.set(1.1 - breath * 0.02, 0.8 + breath * 0.035, 1 - breath * 0.02);
    this.lump.rotation.y = this.time * 0.15;
    const flicker = 0.75 + 0.15 * Math.sin(this.time * 9.3) + 0.1 * Math.sin(this.time * 23.7);
    FIRE.color.setRGB(1, 0.42 + 0.14 * flicker, 0.18 * flicker);
    this.fire.scale.y = 0.85 + 0.15 * flicker;
  }

  render(renderer: THREE.WebGLRenderer): void {
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight, a = this.area();
    renderer.setScissorTest(true);
    renderer.setScissor(a.left, h - a.top - a.height, a.width, a.height);
    renderer.setViewport(a.left, h - a.top - a.height, a.width, a.height);
    renderer.render(this.scene, this.camera);
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, w, h);
  }

  private box(
    parent: THREE.Object3D,
    [w, h, d]: [number, number, number],
    [x, y, z]: [number, number, number],
    m: THREE.Material,
  ): THREE.Mesh {
    const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
    b.position.set(x, y, z);
    parent.add(b);
    return b;
  }

  private buildRoom(): void {
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(16, 10),
      new THREE.MeshStandardMaterial({ map: floorTexture(), roughness: 0.8 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.z = 1;
    const wall = new THREE.Mesh(new THREE.PlaneGeometry(16, 7), PLASTER);
    wall.position.set(0, 3.5, -3.2);
    this.scene.add(floor, wall);
    // Wainscot along the wall, and a window with the sky in it.
    this.box(this.scene, [16, 0.9, 0.06], [0, 0.45, -3.15], WOOD_DARK);
    const sky = new THREE.Mesh(
      new THREE.PlaneGeometry(1.6, 1.1),
      new THREE.MeshBasicMaterial({ color: 0xcfe8f2 }),
    );
    sky.position.set(-0.4, 2.35, -3.17);
    this.scene.add(sky);
    for (
      const [w, h, x, y] of [
        [1.8, 0.1, -0.4, 2.95],
        [1.8, 0.1, -0.4, 1.75],
        [0.1, 1.3, -1.25, 2.35],
        [0.1, 1.3, 0.45, 2.35],
        [0.06, 1.1, -0.4, 2.35],
      ]
    ) {
      this.box(this.scene, [w, h, 0.08], [x, y, -3.12], WOOD);
    }
  }

  /** The workbench with the clay, a rolling pin and the tray of letters; returns the clay. */
  private buildBench(): THREE.Mesh {
    const bench = new THREE.Group();
    this.box(bench, [2.2, 0.1, 1.0], [0, 0.9, 0], WOOD);
    for (const x of [-1, 1]) {
      for (const z of [-0.42, 0.42]) this.box(bench, [0.09, 0.85, 0.09], [x, 0.43, z], WOOD_DARK);
    }
    this.scene.add(bench);

    // The lump of clay: free kneading.
    const lump = new THREE.Mesh(new THREE.SphereGeometry(0.28, 48, 32), CLAY);
    lump.position.set(-0.25, 1.13, 0.05);
    this.spots.free.add(lump);
    const pin = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.5, 24), WOOD);
    pin.rotation.z = Math.PI / 2;
    pin.rotation.y = 0.3;
    pin.position.set(0.25, 0.995, 0.3);
    bench.add(pin);

    // The letters: a tray with envelopes, the top one sealed.
    const letters = this.spots.letters;
    this.box(letters, [0.5, 0.04, 0.36], [0.62, 0.97, -0.15], WOOD_DARK);
    [0, 1, 2].forEach((k) => {
      const e = this.box(letters, [0.42, 0.014, 0.28], [0.62, 1.0 + k * 0.016, -0.15], PAPER);
      e.rotation.y = (k - 1) * 0.12;
    });
    const seal = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.012, 20), SEAL);
    seal.position.set(0.62, 1.04, -0.15);
    letters.add(seal);

    // How to knead: a notebook lying open at the front left.
    const book = this.spots.howto;
    book.position.set(-0.72, 0.95, 0.22);
    book.rotation.y = 0.25;
    this.box(book, [0.46, 0.02, 0.32], [0, 0.01, 0], COVER);
    for (const side of [-1, 1]) {
      const page = this.box(book, [0.21, 0.014, 0.29], [side * 0.108, 0.027, 0], PAPER);
      page.rotation.z = side * -0.06;
      // The lines tilt with the page, just above it.
      for (const z of [-0.08, -0.03, 0.02, 0.07]) {
        this.box(page, [0.15, 0.002, 0.012], [side * 0.002, 0.0085, z], INK);
      }
    }
    return lump;
  }

  /** The kiln at the back left; returns the fire in its mouth. */
  private buildKiln(): THREE.Mesh {
    const kiln = new THREE.Group();
    kiln.position.set(-2.7, 0, -2.3);
    kiln.rotation.y = 0.4;
    this.box(kiln, [1.3, 1.1, 1.1], [0, 0.55, 0], BRICK);
    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(0.66, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2),
      BRICK,
    );
    dome.scale.set(1, 0.75, 0.85);
    dome.position.y = 1.1;
    kiln.add(dome);
    const chimney = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.14, 1.2, 20), BRICK_DARK);
    chimney.position.set(0.25, 1.8, -0.25);
    kiln.add(chimney);
    this.box(kiln, [0.56, 0.5, 0.04], [0, 0.5, 0.56], BRICK_DARK);
    const fire = new THREE.Mesh(new THREE.CircleGeometry(0.2, 24, 0, Math.PI), FIRE);
    fire.position.set(0, 0.3, 0.59);
    kiln.add(fire);
    this.scene.add(kiln);
    return fire;
  }

  /** A framed certificate on the wall beside the window: the credits. */
  private buildCertificate(): void {
    const frame = this.spots.credits;
    frame.position.set(1.2, 2.35, -3.15);
    this.box(frame, [0.78, 0.58, 0.05], [0, 0, 0], WOOD_DARK);
    this.box(frame, [0.66, 0.46, 0.02], [0, 0, 0.03], PAPER);
    // A heading, lines of small print, and the seal.
    this.box(frame, [0.34, 0.04, 0.005], [0, 0.14, 0.045], INK);
    for (const y of [0.04, -0.02, -0.08]) this.box(frame, [0.46, 0.015, 0.005], [0, y, 0.045], INK);
    const seal = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.01, 24), SEAL);
    seal.rotation.x = Math.PI / 2;
    seal.position.set(0.22, -0.15, 0.045);
    frame.add(seal);
  }

  /** A rack of tools on the wall left of the window: the settings. */
  private buildRack(): void {
    const rack = this.spots.settings;
    rack.position.set(-1.8, 2.2, -3.15);
    this.box(rack, [0.72, 0.07, 0.05], [0, 0.2, 0.02], WOOD_DARK);
    for (const x of [-0.22, 0, 0.22]) {
      const peg = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.07, 12), WOOD);
      peg.rotation.x = Math.PI / 2;
      peg.position.set(x, 0.2, 0.07);
      rack.add(peg);
    }
    // Hanging from the pegs: a wooden spatula, a wire loop tool and a sponge.
    const spatula = new THREE.Group();
    spatula.position.set(-0.22, 0.19, 0.08);
    spatula.rotation.z = 0.06;
    this.box(spatula, [0.035, 0.24, 0.014], [0, -0.13, 0], WOOD);
    this.box(spatula, [0.085, 0.13, 0.01], [0, -0.3, 0], WOOD);
    rack.add(spatula);
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.2, 16), WOOD);
    handle.position.set(0, 0.07, 0.08);
    const loop = new THREE.Mesh(new THREE.TorusGeometry(0.045, 0.006, 8, 24), METAL);
    loop.position.set(0, -0.08, 0.08);
    rack.add(handle, loop);
    this.box(rack, [0.13, 0.1, 0.05], [0.22, 0.1, 0.08], SPONGE).rotation.z = -0.08;
  }

  /** The shelf at the back right, with room for six pieces, and the ledger on top. */
  private buildShelf(): void {
    const shelf = this.spots.shelf;
    shelf.position.set(2.6, 0, -2.4);
    shelf.rotation.y = -0.45;
    // The ledger: a thick book lying on top, its label facing the room.
    const ledger = this.spots.ledger;
    ledger.position.copy(shelf.position);
    ledger.rotation.copy(shelf.rotation);
    // Its boards and spine, and the pages set a little in from them (a face shared by two
    // parts of different colours flickers, so none is: each part sinks into the next).
    for (const y of [2.096, 2.184]) {
      this.box(ledger, [0.5, 0.012, 0.355], [-0.3, y, 0.0175], LEATHER);
    }
    this.box(ledger, [0.5, 0.098, 0.014], [-0.3, 2.14, 0.193], LEATHER);
    this.box(ledger, [0.49, 0.078, 0.342], [-0.3, 2.14, 0.016], PAPER);
    this.box(ledger, [0.22, 0.05, 0.004], [-0.3, 2.14, 0.201], PAPER);
    // The back stops inside the top board, short of the sides' tops.
    this.box(shelf, [1.6, 2.07, 0.06], [0, 1.035, -0.25], WOOD_DARK);
    for (const x of [-0.8, 0.8]) this.box(shelf, [0.08, 2.1, 0.55], [x, 1.05, 0], WOOD);
    for (const y of [0.05, 0.72, 1.39, 2.06]) this.box(shelf, [1.66, 0.06, 0.55], [0, y, 0], WOOD);
  }

  /** Puts the latest pieces on the shelf, small, on their pedestals. */
  private fillShelf(pieces: Piece[]): void {
    const shared = new Set(appearanceMaterials());
    for (const o of this.shown.children) {
      const m = o as THREE.Mesh;
      m.geometry.dispose();
      const material = m.material as THREE.Material;
      if (!shared.has(material)) material.dispose();
    }
    this.shown.clear();
    pieces.forEach((piece, k) => {
      const x = k % 2 ? 0.38 : -0.38, y = [1.42, 0.75, 0.08][Math.floor(k / 2)];
      const pedestal = new THREE.Mesh(
        new THREE.CylinderGeometry(0.17, 0.18, 0.06, 32),
        pedestalMaterial(piece.rank),
      );
      pedestal.position.set(x, y + 0.03, 0);
      const g = new THREE.BufferGeometry();
      g.setIndex(new THREE.BufferAttribute(piece.index, 1));
      g.setAttribute("position", new THREE.BufferAttribute(piece.positions.slice(), 3));
      g.computeBoundingBox();
      const box = g.boundingBox!, size = box.getSize(new THREE.Vector3());
      const c = box.getCenter(new THREE.Vector3()), s = 0.4 / Math.max(size.x, size.y, size.z);
      g.translate(-c.x, -box.min.y, -c.z);
      g.scale(s, s, s);
      g.computeVertexNormals();
      const mesh = new THREE.Mesh(g, pieceMaterial(piece));
      mesh.position.set(x, y + 0.06, 0);
      this.shown.add(pedestal, mesh);
    });
  }
}
